import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { PermissionMatrix, AccessLevel, PageKey, AdminPermissionPayload } from '@/types/permissions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';

const ROLES = ['ADMIN', 'PM', 'USER'];
const PAGE_KEYS: PageKey[] = [
  'dashboard', 'projects', 'clients', 'teams', 'time-entries', 'timesheets',
  'planned-hours', 'budgets', 'reports', 'exports', 'templates', 'admin',
];
const LEVELS: AccessLevel[] = ['NONE', 'VIEWOWN', 'READ', 'READWRITE', 'ADMIN'];

export default function PermissionsMatrix() {
  const [matrix, setMatrix] = useState<PermissionMatrix>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<AdminPermissionPayload[]>('/permissions/admin/permissions')
      .then((data) => {
        const m: PermissionMatrix = {};
        for (const p of data || []) {
          if (!m[p.role]) m[p.role] = {};
          m[p.role][p.pageKey] = p.accessLevel as AccessLevel;
        }
        for (const role of ROLES) {
          if (!m[role]) m[role] = {};
          for (const pk of PAGE_KEYS) {
            if (!m[role][pk]) m[role][pk] = 'NONE';
          }
        }
        setMatrix(m);
      })
      .catch(() => {
        setMatrix({});
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (role: string, pageKey: PageKey, level: AccessLevel) => {
    setMatrix((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [pageKey]: level,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: AdminPermissionPayload[] = [];
      for (const role of ROLES) {
        for (const pk of PAGE_KEYS) {
          const level = matrix[role]?.[pk] || 'NONE';
          payload.push({ role, pageKey: pk, accessLevel: level });
        }
      }
      await api('/permissions/admin/permissions', {
        method: 'PUT',
        body: JSON.stringify({ permissions: payload }),
      });
      alert('Permissions saved successfully');
    } catch {
      alert('Error saving permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 w-full animate-pulse rounded bg-[var(--color-muted)]" />
        <div className="h-64 w-full animate-pulse rounded bg-[var(--color-muted)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-[var(--color-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--color-muted)]">
              <th className="sticky left-0 bg-[var(--color-muted)] px-3 py-2 text-left font-semibold">Role</th>
              {PAGE_KEYS.map((pk) => (
                <th key={pk} className="px-3 py-2 text-center font-semibold capitalize">
                  {pk.replace(/-/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROLES.map((role) => (
              <tr key={role} className="border-t border-[var(--color-border)]">
                <td className="sticky left-0 bg-[var(--color-card)] px-3 py-2 font-semibold">{role}</td>
                {PAGE_KEYS.map((pk) => (
                  <td key={pk} className="px-2 py-2 text-center">
                    <Select
                      value={(matrix[role]?.[pk] as AccessLevel) || 'NONE'}
                      onValueChange={(value) => handleChange(role, pk, value as AccessLevel)}
                    >
                      <SelectTrigger className="h-8 w-20 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((level) => (
                          <SelectItem key={level} value={level} className="text-xs">
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
        <span className="text-xs text-[var(--color-muted-foreground)]">
          Changes apply immediately to all existing sessions.
        </span>
      </div>
    </div>
  );
}
